/** Centralized react-query keys for the Support app. */
export const supportKeys = {
  all: ['support'] as const,
  me: () => [...supportKeys.all, 'me'] as const,
  masters: () => [...supportKeys.all, 'masters'] as const,
  departments: () => [...supportKeys.all, 'departments'] as const,
  watcherRules: () => [...supportKeys.all, 'watcher-rules'] as const,
  tickets: (params?: unknown) => [...supportKeys.all, 'tickets', params ?? {}] as const,
  ticket: (id: number | string) => [...supportKeys.all, 'ticket', String(id)] as const,
  // Ranged keys extend the rangeless one, so invalidating the rangeless key refreshes both.
  ticketStats: (range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'ticket-stats', `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'ticket-stats'] as const),
  // Deliberately under the 'tickets' segment: the WS batcher's root tickets
  // invalidation refreshes the chips with no extra wiring.
  ticketCounts: (params?: unknown) => [...supportKeys.all, 'tickets', 'counts', params ?? {}] as const,
  dashboardStats: (scope?: string, range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'dashboard', 'stats', scope ?? 'default', `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'dashboard', 'stats', scope ?? 'default'] as const),
  dashboardHome: (scope?: string, range?: { from: string; to: string }) =>
    [...supportKeys.all, 'dashboard', 'home', scope ?? 'default', range ? `${range.from}_${range.to}` : 'all'] as const,
  dashboardAnalytics: (weeks?: number, range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'dashboard', 'analytics', weeks ?? 12, `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'dashboard', 'analytics', weeks ?? 12] as const),
  /** One heatmap cell's drill-in. Keyed on the cell AND its window — the same cell
   *  over a different range is a different question, not a stale answer. */
  dashboardBreachCell: (params: { poolId: number; priorityKey: string; range?: { from: string; to: string } }) =>
    [
      ...supportKeys.all,
      'dashboard',
      'breach-cell',
      params.poolId,
      params.priorityKey,
      params.range ? `${params.range.from}_${params.range.to}` : 'default',
    ] as const,
  dashboardDrift: () => [...supportKeys.all, 'dashboard', 'drift'] as const,
  dashboardTransfers: (params?: unknown) => [...supportKeys.all, 'dashboard', 'transfers', params ?? {}] as const,
  dashboardApprovals: () => [...supportKeys.all, 'dashboard', 'approvals'] as const,
  dashboardMyPulse: (range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'dashboard', 'my-pulse', `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'dashboard', 'my-pulse'] as const),
  dashboardWatched: (range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'dashboard', 'watched', `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'dashboard', 'watched'] as const),
  notices: () => [...supportKeys.all, 'notices'] as const,
  activity: (params?: unknown) => [...supportKeys.all, 'activity', params ?? {}] as const,
  /**
   * The CUMULATIVE (page-by-page) activity feed — a different cache shape from
   * `activity` (pages accumulate), so it must never share that key.
   *
   * It keeps the same 3-segment SHAPE on purpose: the ticket-update invalidator
   * (`tickets.query.ts`) reads the params object positionally at index 2 to tell
   * "this ticket's timeline" from "everyone else's". Slipping a `'feed'` segment
   * in front would push params to index 3, and the predicate would silently stop
   * refetching the open ticket's trail. The `cumulative` flag INSIDE the params
   * is what separates the two caches.
   */
  activityFeed: (params?: Record<string, unknown>) =>
    [...supportKeys.all, 'activity', { ...(params ?? {}), cumulative: true }] as const,
  /** Server-persisted activity views (projects-parity). */
  activityViews: () => [...supportKeys.all, 'activity-views'] as const,
  activityViewOptions: () => [...supportKeys.all, 'activity-views', 'options'] as const,
  ticketTypes: () => [...supportKeys.all, 'ticket-types', 'list'] as const,
  /** The full type → category → SKU tree (one key per include_inactive flavor). */
  typeTree: (includeInactive?: boolean) =>
    [...supportKeys.all, 'ticket-types', 'tree', includeInactive ? 'all' : 'active'] as const,
  categories: (ticketTypeId?: number) => [...supportKeys.all, 'categories', ticketTypeId ?? 'all'] as const,
  skus: (params?: unknown) => [...supportKeys.all, 'skus', params ?? {}] as const,
  /** All active queries (GET /skus, no params) — the raise-dialog search source. */
  skusAll: () => [...supportKeys.all, 'skus', 'all'] as const,
  /** Most-raised queries (GET /skus/popular). */
  skusPopular: (params?: unknown) => [...supportKeys.all, 'skus', 'popular', params ?? {}] as const,
  sku: (id: number | string) => [...supportKeys.all, 'sku', String(id)] as const,
  settings: () => [...supportKeys.all, 'settings'] as const,
  /** THE workspace calendar (§4.10). One per workspace, so no id in the key. */
  businessHours: () => [...supportKeys.all, 'business-hours'] as const,
  businessHoursImpact: () => [...supportKeys.all, 'business-hours', 'impact'] as const,
  regional: () => [...supportKeys.all, 'regional'] as const,
  onboarding: () => [...supportKeys.all, 'onboarding'] as const,
  teams: () => [...supportKeys.all, 'teams'] as const,
  team: (id: number | string) => [...supportKeys.all, 'team', String(id)] as const,
  teamMembers: (id: number | string) => [...supportKeys.team(id), 'members'] as const,
  escalationChains: () => [...supportKeys.all, 'escalation-chains'] as const,
  approvalChains: () => [...supportKeys.all, 'approval-chains'] as const,
  pendingApprovals: () => [...supportKeys.all, 'pending-approvals'] as const,
  resolution: () => [...supportKeys.all, 'resolution'] as const,
  resolutionGlobal: () => [...supportKeys.all, 'resolution', 'global'] as const,
  resolutionTeams: () => [...supportKeys.all, 'resolution', 'teams'] as const,
  resolutionTeam: (poolId: number | string) => [...supportKeys.all, 'resolution', 'team', String(poolId)] as const,
  resolutionGaps: (poolId: number | string) => [...supportKeys.all, 'resolution', 'gaps', String(poolId)] as const,
  resolutionGlobalPickup: () => [...supportKeys.all, 'resolution', 'global-pickup'] as const,
  resolutionPolicy: () => [...supportKeys.all, 'resolution', 'policy'] as const,
  myAvailability: () => [...supportKeys.all, 'availability', 'me'] as const,
  myTeams: () => [...supportKeys.all, 'me', 'teams'] as const,
  opsDashboard: (range?: { from: string; to: string }) =>
    range
      ? ([...supportKeys.all, 'dashboard', 'ops', `${range.from}_${range.to}`] as const)
      : ([...supportKeys.all, 'dashboard', 'ops'] as const),
  notifications: (params?: unknown) => [...supportKeys.all, 'notifications', params ?? {}] as const,
  notificationsUnreadCount: () => [...supportKeys.all, 'notifications', 'unread-count'] as const,
  rbacDefinitions: () => [...supportKeys.all, 'rbac', 'definitions'] as const,
  rbacTemplates: () => [...supportKeys.all, 'rbac', 'templates'] as const,
  rbacTemplate: (id: number | string) => [...supportKeys.all, 'rbac', 'template', String(id)] as const,
  rbacUserPerms: () => [...supportKeys.all, 'rbac', 'user-perms'] as const,
  rbacUserPerm: (userId: number | string) => [...supportKeys.all, 'rbac', 'user-perm', String(userId)] as const,
  rbacElevations: () => [...supportKeys.all, 'rbac', 'elevations'] as const,
  rbacAuditLogs: (params?: unknown) => [...supportKeys.all, 'rbac', 'audit-logs', params ?? {}] as const,
} as const;
